import { createAsyncThunk } from '@reduxjs/toolkit';
import { GetTournamentTeams } from 'modules/teams/domain/interfaces/request-dto';
import teamsRepository from 'modules/teams/domain/repositories/teams.repository';
import { RootState } from 'store/store';
import { Team } from 'modules/teams/domain/interfaces/team';
import { UpsertTournamentTeamDto } from 'modules/teams/domain/dtos/upsert-tournament-team.dto';
import teamsResource from 'modules/teams/domain/resources/teams-resource';
import { getNormalizedDate } from 'shared/utils/get-normalized-date';

const PREFIX = 'teams';

export const getTournamentTeams = createAsyncThunk<{ teams: Team[]; tournamentId: number }, Omit<GetTournamentTeams, 'tournamentId'>>(
  `${PREFIX}/fetchTeams`,
  async (payload, { rejectWithValue, getState }) => {
    try {
      const { auth } = getState() as RootState;
      const activeTournamentId = auth.activeTournamentId;

      if (!activeTournamentId) {
        throw new Error('No active tournament');
      }

      const teams = await teamsRepository.getTournamentTeams({ ...payload, tournamentId: activeTournamentId });
      return {
        teams,
        tournamentId: activeTournamentId,
      };
    } catch (error) {
      console.error(`${PREFIX}/fetchTeams error:`, error);

      let message;
      if (error instanceof Error) message = error.message;
      else message = String(error);
      return rejectWithValue(message);
    }
  },
);

export const getTournamentTeamsByFilter = createAsyncThunk<Team[], Omit<GetTournamentTeams, 'tournamentId' | 'offset'>>(
  `${PREFIX}/fetchTeamsByFilter`,
  async (payload, { rejectWithValue, getState }) => {
    try {
      const { auth } = getState() as RootState;
      const activeTournamentId = auth.activeTournamentId;

      if (!activeTournamentId) {
        throw new Error('No active tournament');
      }

      return await teamsRepository.getTournamentTeams({ ...payload, offset: 0, tournamentId: activeTournamentId });
    } catch (error) {
      console.error(`${PREFIX}/fetchTeamsByFilter error:`, error);

      let message;
      if (error instanceof Error) message = error.message;
      else message = String(error);
      return rejectWithValue(message);
    }
  },
);

export const createTeam = createAsyncThunk<Team, UpsertTournamentTeamDto>(
  `${PREFIX}/createTeam`,
  async (payload, { rejectWithValue }) => {
    try {
      const team = { name: payload.name, shortName: payload.shortName, tournamentId: payload.tournamentId };
      const teamData = await teamsRepository.upsertTeam(team);
      const teamId = teamData.id;

      if (payload.logo) {
        const logo: File = Array.isArray(payload.logo) ? payload.logo[0] : payload.logo;
        const res = await teamsResource.uploadLogo(logo, teamId);
        teamData.logo = { id: res.id, url: res.emblem };
      }

      teamData.createdAt = getNormalizedDate(new Date());

      return teamData;
    } catch (error) {
      console.error(`${PREFIX}/createTeam error:`, error);

      let message;
      if (error instanceof Error) message = error.message;
      else message = String(error);
      return rejectWithValue(message);
    }
  },
);

export const editTeam = createAsyncThunk<Team, UpsertTournamentTeamDto>(`${PREFIX}/editTeam`, async (payload, { rejectWithValue }) => {
  try {
    const team = { id: payload.id, name: payload.name, shortName: payload.shortName, tournamentId: payload.tournamentId };
    const teamData = await teamsRepository.upsertTeam(team);
    const teamId = teamData.id;

    if (payload.logo instanceof File ) {
      const logo: File = Array.isArray(payload.logo) ? payload.logo[0] : payload.logo;
      const res = await teamsResource.uploadLogo(logo, teamId);
      teamData.logo = { id: res.id, url: res.emblem };
    }

    return teamData;
  } catch (error) {
    console.error(`${PREFIX}/editTeam error:`, error);

    let message;
    if (error instanceof Error) message = error.message;
    else message = String(error);
    return rejectWithValue(message);
  }
});
